import { useMemo, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTimes } from "react-icons/fa";
import http from "../../api/apiClient";
import { euro, type Allocation, type Receipt } from "./contributionsModel";
import FamilyPicker, { type FamilyOption } from "./FamilyPicker";

type Part = { key: number; familyId: string; childId: string; amount: string };

export const toCents = (value: string): number | null => {
  const text = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
};
export const centsToInput = (cents: number): string => (cents / 100).toFixed(2).replace(".", ",");

/**
 * Divide one payment over families and (optionally) their children, for example a benefactor paying for children
 * of several families. The parts must add up to the payment.
 */
export default function SplitEditor({ receipt, families, suggestion, initial, onDone, onCancel }: {
  receipt: Receipt;
  families: FamilyOption[];
  /** Previous division of an "always ask" payer (shown as such). */
  suggestion?: Allocation[];
  /** Start with these lines plus an empty one, e.g. the family chosen while linking. */
  initial?: Allocation[];
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const nextKey = useRef(0);
  const newPart = (familyId = "", childId = "", amount = ""): Part => ({ key: nextKey.current++, familyId, childId, amount });
  const [parts, setParts] = useState<Part[]>(() => {
    const start = suggestion?.length ? suggestion : receipt.allocations?.length ? receipt.allocations : null;
    if (start) return start.map((a) => newPart(a.familyId, a.childId ?? "", centsToInput(Math.round(Number(a.amount) * 100))));
    // Coming from "link": the chosen family keeps no amount yet, so the amounts are divided on purpose.
    if (initial?.length) return [...initial.map((a) => newPart(a.familyId, a.childId ?? "", "")), newPart()];
    return [newPart(), newPart()];
  });
  // A payer can be remembered by its e-Boekhouden relation or, for a bank line without relation, by its IBAN.
  const hasPayer = Boolean(receipt.relationName || receipt.payerIbanMasked);
  // Off by default; on for a payer that is already set to always ask (its previous division is filled in).
  const [askNextTime, setAskNextTime] = useState(hasPayer && Boolean(suggestion?.length));
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const total = Math.round(Number(receipt.amount) * 100);
  const assigned = parts.reduce((sum, part) => sum + (toCents(part.amount) ?? 0), 0);
  const remaining = total - assigned;
  // Empty lines (no family and no amount) are simply left out.
  const filled = parts.filter((part) => part.familyId || part.amount.trim());
  const incomplete = filled.some((part) => !part.familyId || (toCents(part.amount) ?? 0) <= 0);
  const valid = filled.length > 0 && !incomplete && remaining === 0;
  const familyById = useMemo(() => new Map(families.map((family) => [family.familyId, family])), [families]);

  const update = (key: number, changes: Partial<Part>) =>
    setParts((current) => current.map((part) => (part.key === key ? { ...part, ...changes } : part)));

  const errorText = (code?: string) => {
    switch (code) {
      case "SPLIT_TOTAL_MISMATCH": return t("finance.payments.split.errors.total", { defaultValue: "The parts must add up to the payment." });
      case "SPLIT_AMOUNT_INVALID": return t("finance.payments.split.errors.amount", { defaultValue: "Enter amounts above zero with at most two decimals." });
      case "SPLIT_CHILD_NOT_IN_FAMILY": return t("finance.payments.split.errors.child", { defaultValue: "A chosen child does not belong to that family." });
      default: return t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." });
    }
  };

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      await http.post(`/finance/receipts/${receipt.id}/split`, {
        parts: filled.map((part) => ({
          familyId: part.familyId,
          childId: part.childId || null,
          amount: ((toCents(part.amount) ?? 0) / 100).toFixed(2),
        })),
        askNextTime: askNextTime && hasPayer,
      });
      onDone();
    } catch (caught: any) {
      setMessage(errorText(caught?.response?.data?.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-2 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm dark:border-indigo-400/20 dark:bg-indigo-400/5">
      <p className="font-bold text-slate-900 dark:text-white">
        {t("finance.payments.split.title", { amount: euro(receipt.amount), defaultValue: "Divide {{amount}}" })}
        {suggestion?.length ? (
          <span className="ml-2 text-xs font-normal text-slate-500">
            {t("finance.payments.split.previous", { defaultValue: "previous division filled in" })}
          </span>
        ) : null}
      </p>
      {parts.map((part, index) => {
        const family = familyById.get(part.familyId);
        return (
          <div key={part.key} data-part={index} className="flex flex-wrap items-center gap-2">
            <FamilyPicker
              options={families}
              value={part.familyId}
              withChildren
              onChange={(familyId, childId) => update(part.key, { familyId, childId: childId || "" })}
              label={t("finance.payments.split.family", { number: index + 1, defaultValue: "Family for part {{number}}" })}
            />
            <select
              value={part.childId}
              disabled={!family || !(family.children?.length)}
              onChange={(event) => update(part.key, { childId: event.target.value })}
              aria-label={t("finance.payments.split.child", { number: index + 1, defaultValue: "Child for part {{number}}" })}
              className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm disabled:opacity-50 dark:border-white/10 dark:bg-slate-900"
            >
              <option value="">{t("finance.payments.split.whole_family", { defaultValue: "Whole family" })}</option>
              {(family?.children ?? []).map((child) => <option key={child.childId} value={child.childId}>{child.name}</option>)}
            </select>
            <input
              inputMode="decimal"
              value={part.amount}
              placeholder="0,00"
              onChange={(event) => update(part.key, { amount: event.target.value })}
              aria-label={t("finance.payments.split.amount", { number: index + 1, defaultValue: "Amount for part {{number}}" })}
              className="h-9 w-24 rounded-lg border border-slate-200 bg-white px-2 text-right text-sm tabular-nums dark:border-white/10 dark:bg-slate-900"
            />
            {remaining > 0 && !part.amount && (
              <button type="button" onClick={() => update(part.key, { amount: centsToInput(remaining) })}
                className="text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-300">
                {t("finance.payments.split.rest", { defaultValue: "The rest" })}
              </button>
            )}
            {parts.length > 1 && (
              <button type="button" onClick={() => setParts((current) => current.filter((other) => other.key !== part.key))}
                aria-label={t("finance.payments.split.remove", { number: index + 1, defaultValue: "Remove part {{number}}" })}
                className="rounded-md p-1.5 text-slate-500 hover:bg-white dark:hover:bg-white/10">
                <FaTimes aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => setParts((current) => [...current, newPart()])}
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline dark:text-indigo-300">
          <FaPlus aria-hidden="true" />
          {t("finance.payments.split.add", { defaultValue: "Add a part" })}
        </button>
        <span role="status" className={`text-xs font-semibold tabular-nums ${remaining === 0 ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
          {remaining === 0
            ? t("finance.payments.split.complete", { defaultValue: "Fully divided" })
            : remaining > 0
              ? t("finance.payments.split.remaining", { amount: euro(remaining / 100), defaultValue: "{{amount}} still to divide" })
              : t("finance.payments.split.too_much", { amount: euro(-remaining / 100), defaultValue: "{{amount}} too much" })}
        </span>
      </div>
      {incomplete && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t("finance.payments.split.incomplete", { defaultValue: "Fill in a family and an amount on every line, or leave the line empty." })}
        </p>
      )}
      <label className={`inline-flex items-center gap-2 text-xs ${hasPayer ? "text-slate-700 dark:text-slate-200" : "text-slate-400"}`}>
        <input type="checkbox" checked={askNextTime && hasPayer} disabled={!hasPayer} onChange={(event) => setAskNextTime(event.target.checked)} />
        {t("finance.payments.split.ask_next_time", { defaultValue: "Always ask for this payer, with this division filled in" })}
      </label>
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={!valid || saving}
          className="inline-flex h-9 items-center rounded-lg bg-indigo-600 px-3 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-40">
          {t("finance.payments.split.save", { defaultValue: "Save division" })}
        </button>
        <button type="button" onClick={onCancel}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-white dark:border-white/10 dark:text-slate-300">
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
      </div>
      {message && <p role="alert" className="text-xs font-semibold text-rose-600">{message}</p>}
    </div>
  );
}
